import React from 'react';
import { useSalaryProcessEntry } from './hooks/useSalaryProcessEntry';

export default function SalaryProcessEntryScreen() {
  const { state, actions } = useSalaryProcessEntry();
  const { month, year, company, branch, department, isProcessing, processProgress } = state;

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Top Header / Breadcrumb */}
      <section className="flex flex-col gap-2 border-b border-outline-variant/10 pb-5">
        <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant">
          <span>Payroll</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-primary font-black">Payroll Processing</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-primary font-black">Salary Process Execution</span>
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Salary Process For Month - {month} and Year - {year}
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant max-w-2xl leading-relaxed">
            Execute salary calculations, attendance reconciliations, and pay slip provisions.
          </p>
        </div>
      </section>

      {/* Main Two-Column Layout matching Screenshot 5 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left Form Card (Screenshot 5) */}
        <div className="lg:col-span-8 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest overflow-hidden shadow-xs">
          {/* Card Header Bar */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/80 px-6 py-4">
            <h2 className="text-sm font-bold text-on-surface">
              Salary Process For Month - {month} and Year - {year}
            </h2>
          </div>

          {/* Form Body */}
          <div className="p-6 sm:p-8 space-y-5">
            {/* Company Field */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
              <label className="sm:col-span-3 text-xs font-bold text-on-surface-variant">
                Company
              </label>
              <div className="sm:col-span-9">
                <input
                  type="text"
                  value={company}
                  readOnly
                  disabled
                  className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface opacity-90 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Branch Field */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
              <label className="sm:col-span-3 text-xs font-bold text-on-surface-variant">
                Branch
              </label>
              <div className="sm:col-span-9">
                <input
                  type="text"
                  value={branch}
                  readOnly
                  disabled
                  className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface opacity-90 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Department Field */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
              <label className="sm:col-span-3 text-xs font-bold text-on-surface-variant">
                Department
              </label>
              <div className="sm:col-span-9">
                <input
                  type="text"
                  value={department}
                  readOnly
                  disabled
                  className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface opacity-90 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Processing Progress Bar when active */}
            {isProcessing && (
              <div className="pt-2 space-y-2 animate-in fade-in">
                <div className="flex justify-between text-xs font-bold text-primary">
                  <span>Calculating earnings and statutory deductions...</span>
                  <span>{processProgress}%</span>
                </div>
                <div className="w-full h-2.5 bg-surface-container-high rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 rounded-full"
                    style={{ width: `${processProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons Row matching Screenshot 5 */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-6 py-4">
            <button
              type="button"
              onClick={actions.handleProcess}
              disabled={isProcessing}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <span className="material-symbols-outlined text-sm animate-spin">
                    progress_activity
                  </span>
                  <span>Processing...</span>
                </>
              ) : (
                <span>Process</span>
              )}
            </button>

            <button
              type="button"
              onClick={actions.handleCancel}
              disabled={isProcessing}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right TIPS Panel matching Screenshot 5 */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-3">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">
              lightbulb
            </span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Company, Branch &amp; Department are selected from filters on List screen. To change selection, cancel processing and select again.
          </p>

          <div className="pt-2 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              Verification Checklist:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-1.5 list-disc list-inside">
              <li>Attendance logs for {month} {year} are finalized.</li>
              <li>Approved leaves and loss-of-pay deductions verified.</li>
              <li>Payhead allowance updates are applied.</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}
